import { useEffect, useRef, useState } from "react";
import { useTripStore } from "../../store/tripStore";
import { useUIStore } from "../../store/uiStore";
import { Button } from "../Button";
import { TripTimezoneSelect } from "../TripTimezoneSelect";

interface CalendarDayHeaderProps {
  date: string; // YYYY-MM-DD
  dayIndex: number;
  zone: string;
  defaultTimezone: string;
  isReadOnly: boolean;
}

function formatHeaderDate(date: string): string {
  return new Date(date + "T00:00:00").toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

/** Short label for a zone badge, e.g. "Asia/Tokyo" → "TOKYO" */
function zoneBadgeLabel(zone: string): string {
  const city = zone.split("/").pop() ?? zone;
  return city.replace(/_/g, " ").toUpperCase();
}

/**
 * Column header: "Day N" + date (click focuses the day view on this date),
 * a timezone badge when the column's zone differs from the trip default,
 * and a menu hosting the timezone override.
 */
export function CalendarDayHeader({
  date,
  dayIndex,
  zone,
  defaultTimezone,
  isReadOnly,
}: CalendarDayHeaderProps) {
  const setDayTimezone = useTripStore((s) => s.setDayTimezone);
  const setPlannerMode = useUIStore((s) => s.setPlannerMode);
  const setPlannerFocusDate = useUIStore((s) => s.setPlannerFocusDate);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const hasOverride = zone !== defaultTimezone;

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target;
      const isPortalDialogInteraction =
        target instanceof Element &&
        target.closest('[role="dialog"][aria-modal="true"]') !== null;

      // TripTimezoneSelect opens its catalogue in a portal. Treat that modal
      // as part of this menu so its mousedown does not unmount it before the
      // selected option's click handler runs.
      if (
        menuRef.current &&
        !menuRef.current.contains(target as Node) &&
        !isPortalDialogInteraction
      ) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  return (
    <div ref={menuRef} className="group relative flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => {
          setPlannerFocusDate(date);
          setPlannerMode("day");
        }}
        className="flex cursor-pointer items-baseline gap-1.5 rounded transition-colors hover:text-info"
        title={`Open Day ${dayIndex + 1} in day view`}
        aria-label={`Open ${formatHeaderDate(date)} in day view`}
      >
        <span className="text-sm font-extrabold tracking-tight">
          {formatHeaderDate(date)}
        </span>
        <span className="text-xs font-semibold text-secondary">
          Day {dayIndex + 1}
        </span>
      </button>
      {hasOverride && (
        <span
          className="rounded bg-purple-100 px-1 py-px text-2xs font-extrabold tracking-wider text-purple-700"
          title={`This day runs on ${zone} (trip default: ${defaultTimezone})`}
        >
          {zoneBadgeLabel(zone)}
        </span>
      )}
      {!isReadOnly && (
        <span className="ml-auto reveal-on-card-hover">
          <Button
            variant="icon"
            size="sm"
            icon="more_horiz"
            aria-label={`Day ${dayIndex + 1} options`}
            title="Day options"
            onClick={() => setMenuOpen((open) => !open)}
          />
        </span>
      )}

      {menuOpen && (
        <div className="absolute left-0 top-full z-30 mt-1 w-60 rounded-xl border border-border bg-surface p-3 shadow-lg">
          <label className="form-label" htmlFor={`tz-${date}`}>
            Timezone for this day
          </label>
          <TripTimezoneSelect
            id={`tz-${date}`}
            value={zone}
            date={date}
            onChange={(timezoneId) => {
              setDayTimezone(date, timezoneId);
              setMenuOpen(false);
            }}
            data-testid={`day-timezone-select-${date}`}
          />
          {hasOverride && (
            <Button
              variant="ghost"
              size="sm"
              icon="restart_alt"
              fullWidth
              className="mt-2"
              onClick={() => {
                setDayTimezone(date, null);
                setMenuOpen(false);
              }}
            >
              Reset to trip default ({defaultTimezone})
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
